"use client";

import { CheckIcon } from "@heroicons/react/20/solid";
import {
  ChevronDownIcon,
  Cog6ToothIcon,
  MinusIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { FC, ReactElement, useCallback, useState } from "react";
import { twMerge } from "tailwind-merge";
import { getSocialAccountDisplayName, SocialProviderAccount } from "../hooks/useSocialProviderAccounts";
import { SocialProviderType } from "../types/models";
import AccountAvatar from "./AccountAvatar";
import { providerTypeToIconLabel } from "./AddSocialProviderAccountButton";
import Button, { BUTTON_STYLE } from "./Button";
import DropdownMenu from "./DropdownMenu";
import DropdownMenuDivider from "./DropdownMenuDivider";
import type { AccountCadence } from "./publicationFlow/engine/types";
import { getAccountCadence } from "./publicationFlow/engine/types";

type AccountsSelectDropdownProps = {
  accounts: SocialProviderAccount[];
  selectedAccountIds: string[];
  disabledAccountIds?: string[];
  disabledAccountTooltips?: Record<string, string>;
  /** Opt-in recovery rows: retain the destination without allowing selection or cadence edits. */
  reconnectAccountIds?: string[];
  onReconnectAccount?: (account: SocialProviderAccount) => void;
  /**
   * Hover tooltips for enabled rows (per-video selectors use these to name
   * the radio-swap a same-provider click performs). Disabled rows keep
   * their own tooltip channel.
   */
  accountTooltips?: Record<string, string>;
  /**
   * Toggle an account. Return `false` to close the menu (e.g. a confirm
   * dialog is about to open over it); otherwise the menu stays open so
   * several accounts can be toggled in one gesture.
   */
  onToggleAccount: (accountId: string) => void | false;
  /** Opens the Manage accounts dialog (disconnect + add account). */
  onManageAccounts: () => void;
  /** Includes disconnected accounts omitted from the selectable list. */
  hasAccountsNeedingReconnect?: boolean;
  isLoading?: boolean;
  trigger: ReactElement | "plus";
  positions?: ("top" | "bottom" | "left" | "right")[];
  align?: "start" | "center" | "end";
  size?: "s" | "m";
  accountCadences?: Map<string, AccountCadence>;
  /** Return `false` to close the menu (e.g. a re-plan confirm is opening). */
  onChangeAccountCadence?: (
    accountId: string,
    cadence: AccountCadence,
  ) => void | false;
  hideCadence?: boolean;
  /**
   * Session-level selectors only: when 2+ accounts of one provider are
   * selected they form a distribution pool (each video posts to exactly one
   * member); this renders the explainer under the account list.
   */
  showDistributionHint?: boolean;
  /** Scope disclosure shown only inside each account's cadence editor. */
  cadenceScopeHint?: string;
};

const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"] as const;
const ISO_DAYS = [1, 2, 3, 4, 5, 6, 7] as const;

type CadencePresetValue =
  "three_per_week" | "five_per_week" | "one_per_day" | "two_per_day" | "custom";

const CADENCE_PRESETS: {
  value: CadencePresetValue;
  label: string;
  cadence?: Omit<AccountCadence, "preferredTimeSlotId">;
}[] = [
  {
    value: "three_per_week",
    label: "3 posts/week",
    cadence: { maxPostsPerDay: 1, allowedISODays: [1, 3, 5] },
  },
  {
    value: "five_per_week",
    label: "5 posts/week",
    cadence: { maxPostsPerDay: 1, allowedISODays: [1, 2, 3, 4, 5] },
  },
  {
    value: "one_per_day",
    label: "1 post/day",
    cadence: { maxPostsPerDay: 1, allowedISODays: [1, 2, 3, 4, 5, 6, 7] },
  },
  {
    value: "two_per_day",
    label: "2 posts/day",
    cadence: { maxPostsPerDay: 2, allowedISODays: [1, 2, 3, 4, 5, 6, 7] },
  },
  { value: "custom", label: "Custom" },
];

function matchCadencePreset(cadence: AccountCadence): CadencePresetValue {
  for (const preset of CADENCE_PRESETS) {
    if (!preset.cadence) continue;
    if (
      cadence.maxPostsPerDay === preset.cadence.maxPostsPerDay &&
      cadence.allowedISODays.length === preset.cadence.allowedISODays.length &&
      cadence.allowedISODays.every(
        (d, i) => d === preset.cadence!.allowedISODays[i],
      )
    ) {
      return preset.value;
    }
  }
  return "custom";
}

const CadenceEditor: FC<{
  cadence: AccountCadence;
  onChange: (cadence: AccountCadence) => void;
}> = ({ cadence, onChange }) => {
  const allowedSet = new Set(cadence.allowedISODays);

  const handleToggleDay = useCallback(
    (day: number) => {
      const next = allowedSet.has(day)
        ? cadence.allowedISODays.filter((d) => d !== day)
        : [...cadence.allowedISODays, day].sort((a, b) => a - b);
      if (next.length === 0) return;
      onChange({ ...cadence, allowedISODays: next });
    },
    [cadence, onChange, allowedSet],
  );

  const handleMaxPostsChange = useCallback(
    (delta: number) => {
      const next = Math.max(1, Math.min(5, cadence.maxPostsPerDay + delta));
      onChange({ ...cadence, maxPostsPerDay: next });
    },
    [cadence, onChange],
  );

  return (
    <div
      className="flex flex-col gap-2 p-3"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-500">Posts/day</span>
        <div className="flex items-center gap-0.5">
          <Button
            type="button"
            variant="flat"
            size="s"
            onClick={() => handleMaxPostsChange(-1)}
            disabled={cadence.maxPostsPerDay <= 1}
          >
            <MinusIcon />
          </Button>
          <span
            className={twMerge(
              "text-sm font-medium w-5 text-center",
              BUTTON_STYLE.s.width,
            )}
          >
            {cadence.maxPostsPerDay}
          </span>
          <Button
            type="button"
            variant="flat"
            size="s"
            onClick={() => handleMaxPostsChange(1)}
            disabled={cadence.maxPostsPerDay >= 5}
          >
            <PlusIcon />
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-slate-500">Days</span>
        <div className="flex gap-0.5">
          {ISO_DAYS.map((day, i) => (
            <Button
              key={day}
              type="button"
              variant={allowedSet.has(day) ? "primary" : "tertiary"}
              size="s"
              className={`!w-7 !px-0 justify-center ${
                allowedSet.has(day)
                  ? "!bg-blue-500 !border-blue-500"
                  : "bg-slate-150 !text-slate-500"
              }`}
              onClick={() => handleToggleDay(day)}
            >
              {DAY_LABELS[i]}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

export function formatCadenceLabel(cadence: AccountCadence): string {
  const allDays = cadence.allowedISODays.length === 7;
  if (allDays) return `${cadence.maxPostsPerDay}/day`;
  const totalPerWeek = cadence.maxPostsPerDay * cadence.allowedISODays.length;
  return `${totalPerWeek}/wk`;
}

// Summarizes several accounts' cadences as one label: identical cadences keep
// their label; differing ones collapse to a min–max range. Ranges only compare
// like units, so if any account restricts posting days, all cadences are
// normalized to weekly totals.
export function formatCadenceRangeLabel(cadences: AccountCadence[]): string {
  const labels = cadences.map(formatCadenceLabel);
  if (labels.every((l) => l === labels[0])) return labels[0];
  if (cadences.every((c) => c.allowedISODays.length === 7)) {
    const perDay = cadences.map((c) => c.maxPostsPerDay);
    return `${Math.min(...perDay)}–${Math.max(...perDay)}/day`;
  }
  const perWeek = cadences.map(
    (c) => c.maxPostsPerDay * c.allowedISODays.length,
  );
  return `${Math.min(...perWeek)}–${Math.max(...perWeek)}/wk`;
}

// Overlapping stack of the selected accounts' avatars (provider badge on
// each), used inside dropdown triggers as a glanceable summary of who the
// batch posts to. Caps at 3 faces by default; the rest collapse into a
// "+N" disc — except an overflow of exactly one, which shows the face
// instead: a "+1" disc costs the same width as the face it hides.
const MAX_STACK_AVATARS = 3;
// Match the calendar tile's xs avatar size.
const STACK_DISC_PX = 20;
// A 2px overlap leaves 2px between the fixed 16px provider badges.
const STACK_DISC_PITCH_PX = STACK_DISC_PX - 2;

// How many discs the stack draws for `count` selected accounts: every
// face while count ≤ cap + 1 (a "+1" disc would cost the same width as
// the face it hides), else cap faces plus the "+N" disc.
export function getAccountsStackDiscCount(
  count: number,
  maxAvatars: number = MAX_STACK_AVATARS,
): number {
  return count <= maxAvatars + 1 ? count : maxAvatars + 1;
}

// The face row's layout width for `count` accounts — what a host needs
// to reserve when it swaps the stack for something narrower and must
// not let neighbours reflow.
export function getAccountsStackWidth(
  count: number,
  maxAvatars: number = MAX_STACK_AVATARS,
): number {
  const discs = getAccountsStackDiscCount(count, maxAvatars);
  return discs === 0 ? 0 : STACK_DISC_PX + STACK_DISC_PITCH_PX * (discs - 1);
}

// The separation ring between stacked avatars adopts the trigger button's
// background per state (rest/hover/open) so it always blends in. The trigger
// Button must carry the `group` class for the hover/open variants to apply.
// `ring-*`, not `border-*`: a ring is a box-shadow, so it paints outside the
// disc without growing its box (AccountAvatar's invariant — see its header).
const STACK_RING_BY_TRIGGER = {
  tertiary:
    "ring-background group-hover:ring-surface-secondary group-aria-[expanded=true]:ring-surface-secondary dark:group-hover:ring-surface dark:group-aria-[expanded=true]:ring-surface",
  secondary: "ring-background dark:ring-surface",
} as const;

/** Faces only, 16px by default, at most `maxAvatars` then "+N": the Match schedule
 *  accounts preview and the submit form's Auto post chip. */
export const CompactAccountAvatarStack: FC<{
  accounts: SocialProviderAccount[];
  selectedAccountIds: string[];
  maxAvatars?: number;
  /** 14px in thumbnail badges; 16px in form and button summaries. */
  size?: "xs" | "s";
  ringClassName?: string;
  overflowClassName?: string;
}> = ({
  accounts,
  selectedAccountIds,
  maxAvatars = 4,
  size = "s",
  ringClassName = "ring-tooltip",
  overflowClassName = "bg-slate-600 text-white",
}) => {
  const sizeClassName = size === "xs" ? "size-3.5" : "size-4";
  const selectedAccounts = accounts.filter((account) =>
    selectedAccountIds.includes(account.id),
  );
  const visibleAccounts = selectedAccounts.slice(0, maxAvatars);
  const overflowCount = selectedAccounts.length - visibleAccounts.length;
  return (
    <span className="flex items-center" aria-hidden>
      {visibleAccounts.map((account, index) => (
        <img
          key={account.id}
          src={account.profilePicture || "/images/placeholder.png"}
          alt=""
          className={twMerge(
            "shrink-0 rounded-full bg-slate-200 object-cover ring-1",
            sizeClassName,
            ringClassName,
            index > 0 && "-ml-1",
          )}
        />
      ))}
      {overflowCount > 0 ? (
        <span
          className={twMerge(
            "relative -ml-1 flex items-center justify-center rounded-full text-[8px] font-medium ring-1",
            sizeClassName,
            ringClassName,
            overflowClassName,
          )}
        >
          +{overflowCount}
        </span>
      ) : null}
    </span>
  );
};

export const AccountsAvatarStack: FC<{
  accounts: SocialProviderAccount[];
  selectedAccountIds: string[];
  triggerVariant?: keyof typeof STACK_RING_BY_TRIGGER;
  /** Explicit face order (defaults to the `accounts` prop's order), for a
   *  host whose list below the stack is ordered differently; call sites
   *  without one keep the global account order. */
  orderedAccountIds?: string[];
  /** Face cap before the "+N" disc (default 3). The group header passes
   *  2: its stack is a title qualifier in a row where the date title is
   *  the flex item that truncates, so the qualifier stays narrow. */
  maxAvatars?: number;
}> = ({
  accounts,
  selectedAccountIds,
  triggerVariant = "tertiary",
  orderedAccountIds,
  maxAvatars = MAX_STACK_AVATARS,
}) => {
  const accountById = new Map(accounts.map((account) => [account.id, account]));
  // Selected accounts absent from the explicit order (e.g. locked published
  // accounts, which the card order doesn't cover) trail in global order
  // rather than vanish.
  const orderedAccounts = orderedAccountIds
    ? [
        ...orderedAccountIds
          .map((id) => accountById.get(id))
          .filter(
            (account): account is SocialProviderAccount =>
              account !== undefined,
          ),
        ...accounts.filter(
          (account) => !orderedAccountIds.includes(account.id),
        ),
      ]
    : accounts;
  const selectedAccounts = orderedAccounts.filter((account) =>
    selectedAccountIds.includes(account.id),
  );
  if (selectedAccounts.length === 0) return null;
  // cap + 1 faces beats cap faces + a "+1" disc (same width, one more
  // identity), so the disc only ever reads "+2" or more.
  const visibleAccounts =
    getAccountsStackDiscCount(selectedAccounts.length, maxAvatars) ===
    selectedAccounts.length
      ? selectedAccounts
      : selectedAccounts.slice(0, maxAvatars);
  const overflowCount = selectedAccounts.length - visibleAccounts.length;
  const ringClassName = STACK_RING_BY_TRIGGER[triggerVariant];
  return (
    <div className="flex items-center shrink-0">
      {visibleAccounts.map((account, index) => (
        <AccountAvatar
          key={account.id}
          account={account}
          size="xs"
          className={twMerge(index > 0 && "-ml-0.5")}
          avatarClassName={twMerge("ring-2", ringClassName)}
        />
      ))}
      {overflowCount > 0 ? (
        <div
          className={twMerge(
            // `relative` keeps the disc in the same paint order as the
            // positioned avatar wrappers so it stacks on top of the last one.
            // Sized and ringed exactly like the faces it trails, so it sits on
            // the same 20px grid.
            "relative -ml-0.5 size-5 shrink-0 rounded-full ring-2 bg-slate-200 text-slate-500 dark:bg-white/20 dark:text-foreground-secondary flex items-center justify-center text-[10px] font-medium",
            ringClassName,
          )}
        >
          +{overflowCount}
        </div>
      ) : null}
    </div>
  );
};

export type AccountsSelectListProps = Omit<
  AccountsSelectDropdownProps,
  "trigger" | "positions" | "align" | "size"
> & {
  /** Closes the menu hosting the list: a toggle or a cadence pick that
   *  returns `false`, an outside click out of a nested cadence menu, and
   *  Manage. */
  onClose: () => void;
};

/**
 * The account picker's body: every account as a toggle row (its cadence
 * chip trailing unless hidden), the distribution hint, then Manage. Its
 * own component so a host menu can disclose it as a step of that menu's
 * popover — a short's "…" (ShortPostsMenu) — and still be the very
 * picker AccountsSelectDropdown opens. It mounts with the host's panel
 * and unmounts with it, so the cadence menus below always reopen shut.
 */
export const AccountsSelectList: FC<AccountsSelectListProps> = ({
  accounts,
  selectedAccountIds,
  disabledAccountIds = [],
  disabledAccountTooltips,
  reconnectAccountIds = [],
  onReconnectAccount,
  accountTooltips,
  onToggleAccount,
  onManageAccounts,
  hasAccountsNeedingReconnect = reconnectAccountIds.length > 0,
  isLoading = false,
  accountCadences,
  onChangeAccountCadence,
  hideCadence = false,
  showDistributionHint = false,
  cadenceScopeHint,
  onClose,
}) => {
  const [cadenceDropdownAccountId, setCadenceDropdownAccountId] = useState<
    string | null
  >(null);
  const [isCustomEditorOpen, setIsCustomEditorOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      {isLoading && accounts.length === 0 ? (
        <div className="px-3 py-2 text-xs text-slate-500">
          Loading accounts...
        </div>
      ) : null}
      {accounts.map((account) => {
        const isSelected = selectedAccountIds.includes(account.id);
        if (reconnectAccountIds.includes(account.id)) {
          const name = getSocialAccountDisplayName(account);
          return (
            <div key={account.id} className="group flex w-full min-w-0 items-center rounded-[10px] pr-1 hover:bg-surface-secondary dark:hover:bg-surface">
              <div className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2">
                <AccountAvatar account={account} className="shrink-0"
                  avatarClassName={twMerge("size-[20px] border border-white dark:border-background", isSelected && "ring-2 ring-blue-500 dark:ring-foreground")} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm" title={name}>{name}</p>
                  <p className="text-xs text-slate-500 dark:text-foreground-muted">Disconnected</p>
                </div>
              </div>
              {onReconnectAccount ? (
                <Button type="button" size="s" variant="tertiary" className="shrink-0"
                  aria-label={`Reconnect ${name}`}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    onClose();
                    onReconnectAccount(account);
                  }}>Reconnect</Button>
              ) : null}
            </div>
          );
        }
        const isDisabled = disabledAccountIds.includes(account.id);
        const cadence = accountCadences
          ? getAccountCadence(accountCadences, account.id)
          : null;
        const isCadenceDropdownOpen =
          cadenceDropdownAccountId === account.id;
        const activePreset = cadence ? matchCadencePreset(cadence) : null;

        return (
          <div key={account.id} className="flex flex-col">
            <div
              className={twMerge(
                // Hover bg lives on the wrapper (not dimmed when disabled);
                // the disabled look comes from dimming the content instead.
                "group flex w-full min-w-0 items-center rounded-[10px] pr-1 hover:bg-surface-secondary dark:hover:bg-surface",
              )}
              data-tooltip-id={
                isDisabled || accountTooltips?.[account.id]
                  ? "global"
                  : undefined
              }
              data-tooltip-content={
                isDisabled
                  ? (disabledAccountTooltips?.[account.id] ??
                    "This account is disabled")
                  : accountTooltips?.[account.id]
              }
            >
              <Button
                type="button"
                size="m"
                align="start"
                variant="tertiary"
                disabled={isDisabled}
                onClick={() => {
                  const keepOpen = onToggleAccount(account.id);
                  if (keepOpen === false) onClose();
                }}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                className="flex-1 shrink min-w-0 hover:bg-transparent dark:hover:bg-transparent"
              >
                <Button.Icon>
                  <div
                    className={twMerge(
                      "relative shrink-0 w-7 h-7 -mx-1.5",
                      !isSelected && "grayscale",
                    )}
                  >
                    <AccountAvatar
                      account={account}
                      className="absolute top-[3px] right-[3px]"
                      avatarClassName={twMerge(
                        "size-[20px] border border-white dark:border-background",
                        isSelected &&
                          "ring-2 ring-blue-500 dark:ring-foreground",
                      )}
                    />
                  </div>
                </Button.Icon>
                <Button.Text>
                  <span
                    className={twMerge(
                      !isSelected &&
                        "!text-slate-500 dark:!text-foreground-muted",
                    )}
                  >
                    {account.username || account.name || "Unknown account"}
                  </span>
                </Button.Text>
              </Button>
              <div
                className={twMerge(
                  "flex items-center shrink-0",
                  isDisabled && "opacity-50",
                )}
              >
                {!hideCadence && (
                  <div
                    className={twMerge(
                      !(cadence && isSelected && onChangeAccountCadence) &&
                        "invisible pointer-events-none",
                    )}
                  >
                    <DropdownMenu
                      open={isCadenceDropdownOpen}
                      onOpenChange={(nextOpen) => {
                        setCadenceDropdownAccountId(
                          nextOpen ? account.id : null,
                        );
                        if (!nextOpen) setIsCustomEditorOpen(false);
                      }}
                      onClickOutside={(e) => {
                        if (
                          !(e.target as HTMLElement).closest?.(
                            ".popover-menu",
                          )
                        ) {
                          onClose();
                        }
                      }}
                      size="m"
                      positions={["right", "left"]}
                      align="start"
                      containerClassName="-mt-1.5"
                    >
                      <DropdownMenu.Trigger>
                        <Button
                          // [&.tertiary.tertiary:hover] out-specifies the
                          // popover panel's `[&_button.tertiary:hover]:!bg-slate-100`
                          // override, which would otherwise win and make the
                          // hover invisible against the hovered row bg.
                          className="text-slate-500 [&.tertiary.tertiary:hover]:!bg-slate-200"
                          type="button"
                          variant="tertiary"
                          size="s"
                          data-tooltip-id="global"
                          data-tooltip-content="Posting cadence"
                          data-tooltip-class-name="!z-[9999999999999]"
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <Button.Text>
                            {cadence
                              ? formatCadenceLabel(cadence)
                              : "\u00A0"}
                          </Button.Text>
                          <ChevronDownIcon />
                        </Button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Content>
                        {cadenceScopeHint ? <p className="max-w-56 px-3 pt-2 pb-1 text-xs text-slate-500">{cadenceScopeHint}</p> : null}
                        {CADENCE_PRESETS.filter(
                          (p) => p.value !== "custom",
                        ).map((preset) => (
                          <Button
                            key={preset.value}
                            type="button"
                            size="m"
                            variant="tertiary"
                            align="start"
                            className="w-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                preset.cadence &&
                                onChangeAccountCadence
                              ) {
                                const keepOpen = onChangeAccountCadence(
                                  account.id,
                                  {
                                    ...cadence!,
                                    ...preset.cadence,
                                  },
                                );
                                setCadenceDropdownAccountId(null);
                                if (keepOpen === false) {
                                  onClose();
                                }
                              }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            {preset.label}
                            {activePreset === preset.value && <CheckIcon />}
                          </Button>
                        ))}
                        <DropdownMenu
                          open={isCustomEditorOpen}
                          onOpenChange={setIsCustomEditorOpen}
                          onClickOutside={(e) => {
                            if (
                              !(e.target as HTMLElement).closest?.(
                                ".popover-menu",
                              )
                            ) {
                              onClose();
                            }
                          }}
                          size="m"
                          positions={["right", "left"]}
                          align="start"
                          padding={-6}
                          containerClassName="-mt-1.5"
                        >
                          <DropdownMenu.Trigger>
                            <Button
                              type="button"
                              size="m"
                              variant="tertiary"
                              align="start"
                              className="w-full"
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              Custom
                              {activePreset === "custom" && <CheckIcon />}
                            </Button>
                          </DropdownMenu.Trigger>
                          <DropdownMenu.Content>
                            <CadenceEditor
                              cadence={cadence!}
                              onChange={(nextCadence) => {
                                if (
                                  onChangeAccountCadence?.(
                                    account.id,
                                    nextCadence,
                                  ) === false
                                ) {
                                  onClose();
                                }
                              }}
                            />
                          </DropdownMenu.Content>
                        </DropdownMenu>
                      </DropdownMenu.Content>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {showDistributionHint
        ? (() => {
            const countByProvider = new Map<SocialProviderType, number>();
            for (const id of selectedAccountIds) {
              if (reconnectAccountIds.includes(id)) continue;
              const account = accounts.find((a) => a.id === id);
              if (!account) continue;
              countByProvider.set(
                account.provider,
                (countByProvider.get(account.provider) ?? 0) + 1,
              );
            }
            const pooled = [...countByProvider.entries()].filter(
              ([, count]) => count >= 2,
            );
            if (pooled.length === 0) return null;
            return (
              <div className="flex flex-col gap-0.5 px-3 py-1.5 max-w-56">
                {pooled.map(([provider, count]) => (
                  <span key={provider} className="text-xs text-slate-500">
                    Each video posts to one of your {count}{" "}
                    {providerTypeToIconLabel[provider].label} accounts
                  </span>
                ))}
              </div>
            );
          })()
        : null}

      {/* The list's gap-1 already spaces the divider; drop its own my-1 so
          it gets 4px/side like dividers in the app's other (gapless) menus. */}
      <DropdownMenuDivider className="!my-0" />
      <Button
        type="button"
        size="m"
        variant="tertiary"
        align="start"
        className="w-full"
        onClick={() => {
          onClose();
          onManageAccounts();
        }}
      >
        <Cog6ToothIcon />
        Manage
        {hasAccountsNeedingReconnect ? (
          <Button.Content>
            <span className="ml-auto shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-500/10 dark:text-amber-400">
              Need action
            </span>
          </Button.Content>
        ) : null}
      </Button>
    </div>
  );
};

const AccountsSelectDropdown: FC<AccountsSelectDropdownProps> = ({
  trigger,
  positions = ["bottom"],
  align = "center",
  size = "m",
  ...listProps
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  return (
    <DropdownMenu
      size={size}
      positions={positions}
      align={align}
      open={isMenuOpen}
      onOpenChange={setIsMenuOpen}
    >
      <DropdownMenu.Trigger>
        {trigger === "plus" ? (
          <Button
            variant="tertiary"
            size="m"
            data-tooltip-id="global"
            data-tooltip-content="Add account"
          >
            <PlusIcon className="w-4 h-4" />
          </Button>
        ) : (
          trigger
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        <AccountsSelectList
          {...listProps}
          onClose={() => setIsMenuOpen(false)}
        />
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

export default AccountsSelectDropdown;
